/**
 * The dashboard — design/PROTOTYPE-SPEC.md §3.
 *
 * The out-of-date banner, four figures, the Reorder-now and Expiring-soon
 * cards, and the product table whose every row opens the drawer.
 *
 * Nothing here classifies stock. Statuses and counts arrive from the API already
 * decided by core, so this file cannot invent a fifth opinion about what "low"
 * means — see docs/DECISIONS.md §3 for what happens when two places decide.
 */
import { useCallback, useEffect, useState } from 'react';
import { getDashboard, listProducts, type DashboardResponse, type ProductRow } from '../lib/api.js';
import { c, mono, sans, segTab, segTrack } from '../theme.js';
import { Pill, cardStyle, money } from './format.js';
import { ProductDrawer } from './ProductDrawer.js';

type Filter = 'all' | 'reorder' | 'expiring';

export function DashboardPage() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [openProductId, setOpenProductId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * One loader for both calls, reused after every write. The drawer can change
   * stock levels, which changes the KPIs behind it, so they must refresh
   * together or the number on the card contradicts the drawer that changed it.
   */
  const load = useCallback(async () => {
    try {
      const [dash, list] = await Promise.all([getDashboard(), listProducts()]);
      setData(dash);
      // Every row arrives with its status already decided by the API, so the
      // table cannot disagree with the cards above it.
      setRows(list.products);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p style={{ color: c.muted, fontSize: 14 }}>Loading your stock…</p>;
  }
  if (error || !data) {
    return (
      <div style={{ ...cardStyle, borderColor: c.dangerBorder, background: c.dangerBg }}>
        <p style={{ margin: 0, color: c.danger, fontSize: 14 }}>{error}</p>
      </div>
    );
  }

  const { kpis, cards, alert } = data;

  const visible = rows.filter((r) => {
    // The same rule as the figure above it: none left, or at or below minimum.
    if (filter === 'reorder') return r.status.stock === 'out' || r.status.stock === 'reorder';
    if (filter === 'expiring') return ['expired', 'urgent', 'soon'].includes(r.status.expiry);
    return true;
  });

  return (
    <>
      {alert.shouldAlert && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerLine}`,
            borderRadius: 18,
            padding: '14px 18px',
            marginBottom: 12,
            fontSize: 15,
            color: '#5A2511',
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {alert.expiredProducts} product{alert.expiredProducts === 1 ? ' has' : 's have'} stock
            that is out of date.
          </span>{' '}
          Take it off the shelf and write it off.
        </div>
      )}

      {/* One joined strip, not four tiles (27 October 2026): four boxes in four
          colours competed with each other. Colour now lives only in the small
          tag beside a count that needs attention. */}
      <section
        data-tour="kpis"
        className="gs-kpis"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          marginBottom: 12,
        }}
      >
        <Kpi
          label="Stock on hand"
          sub={`${kpis.totalUnits} units, ${kpis.productCount} product${
            kpis.productCount === 1 ? '' : 's'
          }`}
          value={money(kpis.stockValuePence)}
        />
        {/* Amber when something is going out of date; plain when nothing is. */}
        <Kpi
          tone={kpis.expiringCount > 0 ? 'amber' : 'plain'}
          label="Going out of date"
          sub="within 12 weeks"
          value={String(kpis.expiringCount)}
          unit={kpis.expiringCount === 1 ? 'product' : 'products'}
        />
        <Kpi
          tone={kpis.reorderCount > 0 ? 'danger' : 'plain'}
          label="Reorder now"
          sub="at or below minimum"
          value={String(kpis.reorderCount)}
          unit={kpis.reorderCount === 1 ? 'product' : 'products'}
        />
        <Kpi
          label="In date and stocked"
          sub="nothing to do"
          value={String(kpis.healthyCount)}
          unit={kpis.healthyCount === 1 ? 'product' : 'products'}
        />
      </section>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 28,
        }}
      >
        <ActionCard
          title="Reorder now"
          empty="Nothing to reorder."
          total={cards.reorder.total}
          products={cards.reorder.products}
          onOpen={setOpenProductId}
          render={(p) => `${p.qtyOnHand} left · min ${p.minLevel}`}
          tone={(p) => p.status.stock}
          toneLabel={(p) => p.status.stockLabel}
        />
        <ActionCard
          title="Expiring soon"
          empty="Nothing expiring in the next 12 weeks."
          total={cards.expiring.total}
          products={cards.expiring.products}
          onOpen={setOpenProductId}
          render={(p) => `${p.qtyOnHand} left · ${p.status.expiryDate}`}
          tone={(p) => p.status.expiry}
          toneLabel={(p) => p.status.expiryLabel}
        />
      </section>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 300, letterSpacing: '-0.7px' }}>
          Every product
        </h2>
        <div style={segTrack}>
          {(
            [
              ['all', 'All', rows.length],
              ['reorder', 'Reorder', kpis.reorderCount],
              ['expiring', 'Expiring', kpis.expiringCount],
            ] as [Filter, string, number][]
          ).map(([key, labelText, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              style={segTab(filter === key)}
            >
              {labelText}{' '}
              <span style={{ fontFamily: mono, fontSize: 11.5, opacity: 0.75 }}>{count}</span>
            </button>
          ))}
        </div>
      </div>

      <ProductTable rows={visible} onOpen={setOpenProductId} />

      {openProductId && (
        <ProductDrawer
          productId={openProductId}
          onClose={() => setOpenProductId(null)}
          onChanged={load}
        />
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  unit,
  sub,
  tone = 'plain',
}: {
  label: string;
  value: string;
  /** "products" after a count. Money needs none. */
  unit?: string;
  sub: string;
  /** Amber or danger puts `sub` in a small tag beside the number. */
  tone?: 'amber' | 'danger' | 'plain';
}) {
  const tag =
    tone === 'amber'
      ? { bg: c.amberBg, fg: c.amberText, line: c.amberLine }
      : tone === 'danger'
        ? { bg: c.dangerBg, fg: c.danger, line: c.dangerLine }
        : null;
  const below = tag ? unit : [unit, sub].filter(Boolean).join(', ');
  return (
    <div className="gs-kpi" style={{ padding: '18px 22px', minWidth: 0 }}>
      <div style={{ fontSize: 13, color: c.muted }}>{label}</div>
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginTop: 6 }}
      >
        <span style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
          {value}
        </span>
        {tag && (
          <span
            style={{
              fontSize: 12,
              padding: '1px 7px',
              borderRadius: 6,
              background: tag.bg,
              color: tag.fg,
              border: `1px solid ${tag.line}`,
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </span>
        )}
      </div>
      {below && <div style={{ fontSize: 12.5, color: c.faint, marginTop: 2 }}>{below}</div>}
    </div>
  );
}

function ActionCard({
  title,
  empty,
  total,
  products,
  onOpen,
  render,
  tone,
  toneLabel,
}: {
  title: string;
  empty: string;
  total: number;
  products: ProductRow[];
  onOpen: (id: string) => void;
  render: (p: ProductRow) => string;
  tone: (p: ProductRow) => string;
  toneLabel: (p: ProductRow) => string;
}) {
  return (
    <div style={{ background: c.card, borderRadius: 18, padding: '20px 24px 10px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          paddingBottom: 12,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <h2 style={{ fontWeight: 300, fontSize: 22, letterSpacing: '-0.7px', margin: 0 }}>
          {title}
        </h2>
        {total > products.length && (
          <span style={{ fontFamily: mono, fontSize: 11, color: c.faint }}>
            showing {products.length} of {total}
          </span>
        )}
      </div>

      {products.length === 0 ? (
        <p style={{ fontSize: 14.5, color: c.muted, margin: 0, padding: '14px 0 10px' }}>{empty}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {products.map((p, i) => (
            <li
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 0',
                borderTop: i === 0 ? 'none' : '1px solid #EFEFEA',
              }}
            >
              <button
                type="button"
                onClick={() => onOpen(p.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: sans,
                  color: c.ink,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 13, color: c.muted, marginTop: 1 }}>{render(p)}</div>
              </button>
              <Pill tone={tone(p) as never}>{toneLabel(p)}</Pill>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductTable({ rows, onOpen }: { rows: ProductRow[]; onOpen: (id: string) => void }) {
  if (rows.length === 0) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, fontSize: 14.5, color: c.muted }}>
          No products here yet. Add stock to see it on your dashboard.
        </p>
      </div>
    );
  }

  const cell = { padding: '15px 24px' } as const;

  return (
    <div style={{ background: c.card, borderRadius: 18, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead>
            <tr>
              {['product', 'on hand', 'stock', 'use by', 'value', ''].map((h, i) => (
                <th
                  key={h || i}
                  style={{
                    textAlign: 'left',
                    padding: '14px 24px 10px',
                    fontSize: 12.5,
                    color: c.faint,
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${c.border}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr
                key={p.id}
                className="gs-row"
                onClick={() => onOpen(p.id)}
                style={{
                  borderTop: i === 0 ? 'none' : '1px solid #EFEFEA',
                  cursor: 'pointer',
                }}
              >
                <td style={cell}>
                  <div style={{ fontWeight: 500 }}>{p.name}</div>
                  {(p.brand || p.classificationLabel) && (
                    <div style={{ fontSize: 13, color: c.muted, marginTop: 1 }}>
                      {[p.brand, p.classificationLabel].filter(Boolean).join(', ')}
                    </div>
                  )}
                </td>
                <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                  {p.qtyOnHand}
                  <span style={{ color: c.faint, fontSize: 13 }}> of min {p.minLevel}</span>
                </td>
                <td style={cell}>
                  <Pill tone={p.status.stock}>{p.status.stockLabel}</Pill>
                </td>
                <td style={cell}>
                  <Pill tone={p.status.expiry}>{p.status.expiryLabel}</Pill>
                </td>
                <td style={{ ...cell, whiteSpace: 'nowrap' }}>{money(p.stockValuePence)}</td>
                <td style={{ ...cell, textAlign: 'right', padding: '4px 24px' }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(p.id);
                    }}
                    style={{
                      fontFamily: sans,
                      fontSize: 14,
                      fontWeight: 500,
                      height: 34,
                      padding: '0 14px',
                      border: 'none',
                      borderRadius: 980,
                      background: '#EEF2EF',
                      color: c.green,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Log use
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`.gs-row:hover{background:#F7F7F4}.gs-kpi+.gs-kpi{border-left:1px solid ${c.border}}@media (max-width:1100px){.gs-kpis{grid-template-columns:repeat(2,minmax(0,1fr)) !important}.gs-kpi:nth-child(3){border-left:none !important}.gs-kpi:nth-child(n+3){border-top:1px solid ${c.border}}}`}</style>
    </div>
  );
}
